// Pure helpers for the Member Engagement & Response Tracker.
// Ported from the standalone tracker; functions that depend on the reference
// "now" take it as an explicit argument so the page can use the server snapshot.

// The product-wide USD->INR rate. Single source; see src/config/config.js.
import { USD_INR_RATE } from '../../config/config';

export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const PROFILE_BASE = 'https://members.favordating.com/u/';

export const slug = (x = '') => x.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
export const cap = (x = '') => x.charAt(0).toUpperCase() + x.slice(1);

// CSS-safe chip class for a plan key. Class names are case-sensitive, so `goldF`
// would never match a lower-cased rule.
export const planCls = (key = '') => String(key).toLowerCase();

// Normalised monthly price.
//
// The server now sends `rateINR` ALREADY normalised to a monthly figure in INR
// (it divides the member's real charge by the plan's duration). Dividing that by
// the cycle again would report a quarterly plan at a third of its true rate, so
// the cycle maths below is only the legacy path for a payload without `rateINR`.
export const monthlyOf = (sub = {}) => {
  if (sub.rateINR != null) return sub.rateINR;
  if (!sub.price) return 0;
  if (sub.cycle === 'yr') return Math.round(sub.price / 12);
  if (sub.cycle === 'qtr') return Math.round(sub.price / 3);
  return sub.price;
};

// Fallback symbols for values that aren't valid ISO 4217 codes.
const CURRENCY_SYMBOLS = { USD: '$', INR: '₹', EUR: '€', GBP: '£', AUD: 'A$', CAD: 'C$', AED: 'د.إ' };

// Convert an amount in `currency` into INR.
//
// LEGACY FALLBACK ONLY — the engagement endpoint now returns every money figure
// already converted to INR (server-side, via helpers/genuineSubscribers.js
// `toINR`), so this should always be handed INR and act as a no-op. It is kept
// for older payloads and any caller that still passes a foreign amount.
//
// The USD rate is NOT written here. It comes from src/config/config.js, which
// mirrors the server's single source (helpers/currency.js, env `USD_INR_RATE`).
// This file used to carry its own 95 while the server's canon was 90 — two rates
// for one product, so the same member's lifetime value differed depending on
// which surface you read.
//
// The other currencies stay literal: they have no server-side counterpart and no
// live payment has ever arrived in one. They are a display fallback, not canon.
const TO_INR_RATES = { INR: 1, USD: USD_INR_RATE, EUR: 100, GBP: 118, AUD: 62, CAD: 69, AED: 26 };
export const toINR = (amount, currency = 'USD') => {
  const code = (currency || 'USD').toUpperCase();
  const rate = TO_INR_RATES[code] != null ? TO_INR_RATES[code] : TO_INR_RATES.USD;
  return (amount || 0) * rate;
};

export const money = (n, currency = 'USD') => {
  const amt = Math.round(n || 0);
  const code = (currency || 'USD').toUpperCase();
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      maximumFractionDigits: 0,
    }).format(amt);
  } catch (e) {
    const sym = CURRENCY_SYMBOLS[code];
    return (sym || code + ' ') + amt.toLocaleString('en-US');
  }
};

export const monthsSince = (d, now) => Math.max(1, Math.round((now - new Date(d)) / (30 * 864e5)));
export const daysUntil = (d, now) => Math.round((new Date(d) - now) / 864e5);

export const fmtDate = (d) => {
  const x = new Date(d);
  // A missing / unparseable date reads as a dash rather than "NaN undefined NaN".
  if (!d || Number.isNaN(x.getTime())) return '—';
  return x.getDate() + ' ' + MONTHS[x.getMonth()] + ' ' + x.getFullYear();
};
export const fmtMon = (d) => {
  const x = new Date(d);
  return MONTHS[x.getMonth()] + ' ' + x.getFullYear();
};

export const T = (s) => new Date(s).getTime();

export const fmtDur = (min) => {
  if (min < 1) return '<1m';
  if (min < 60) return Math.round(min) + 'm';
  if (min < 1440) {
    const h = Math.floor(min / 60);
    const m = Math.round(min % 60);
    return h + 'h' + (m ? ' ' + m + 'm' : '');
  }
  const d = Math.floor(min / 1440);
  const h = Math.round((min % 1440) / 60);
  return d + 'd' + (h ? ' ' + h + 'h' : '');
};

export const fmtWhen = (s) => {
  const d = new Date(s);
  let h = d.getHours();
  const m = d.getMinutes();
  const ap = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return MONTHS[d.getMonth()] + ' ' + d.getDate() + ', ' + h + ':' + String(m).padStart(2, '0') + ' ' + ap;
};

export const ago = (s, now) => {
  const min = (now - new Date(s)) / 60000;
  if (min < 60) return Math.round(min) + 'm ago';
  if (min < 1440) return Math.round(min / 60) + 'h ago';
  return Math.round(min / 1440) + 'd ago';
};

export const median = (a) => {
  if (!a.length) return null;
  const s = [...a].sort((x, y) => x - y);
  const n = s.length;
  return n % 2 ? s[(n - 1) / 2] : (s[n / 2 - 1] + s[n / 2]) / 2;
};

// First match reply latency (minutes) for a conversation, or null if no reply.
//
// `c.stats` is the server's whole-thread figure and is always preferred: the
// transcript in `c.msgs` is capped at the most recent 60 messages, so deriving
// the FIRST reply from it measures the 60th-from-last message instead of the
// opening one on any long thread. The msgs walk stays as the fallback for a
// payload that predates `stats`.
export const firstReplyLatency = (c) => {
  if (c.stats) return c.stats.lat;
  let lastMe = null;
  for (const m of c.msgs) {
    if (m.f === 'me') {
      if (lastMe === null) lastMe = T(m.t);
    } else if (lastMe !== null) {
      return (T(m.t) - lastMe) / 60000;
    }
  }
  return null;
};

export const lastMsgTime = (c) => (c.stats ? T(c.stats.lastAt) : Math.max(...c.msgs.map((m) => T(m.t))));
export const hasReply = (c) => (c.stats ? c.stats.recv > 0 : c.msgs.some((m) => m.f === 'them'));
// True length of a conversation — `msgs` only carries the last 60.
export const msgCount = (c) => (c.stats ? c.stats.total : c.msgs.length);

// Threads the member actually spoke in. `convos` also carries INBOUND threads —
// ones where somebody messaged them and they never replied — and those are listed
// in the drawer but must stay out of every response figure: the rate measures how
// the member's OWN outreach performs, so its denominator is outbound threads.
// Counting inbound threads would drop each member's rate by however many bots
// pinged them once, which is not a change in their behaviour.
export const outboundConvos = (m) => (m.convos || []).filter((c) => !c.inbound);
export const inboundConvos = (m) => (m.convos || []).filter((c) => c.inbound);

// Per-member computed metrics (independent of "now").
export const metrics = (m) => {
  let sent = 0;
  let replies = 0;
  const lats = [];
  let last = 0;
  const spoke = outboundConvos(m);
  for (const c of spoke) {
    // Counts come from the server's whole-thread stats, NOT the trimmed
    // transcript — otherwise "Sent" silently caps at 60 per conversation.
    sent += c.stats ? c.stats.sent : c.msgs.filter((x) => x.f === 'me').length;
    replies += c.stats ? c.stats.recv : c.msgs.filter((x) => x.f === 'them').length;
    const l = firstReplyLatency(c);
    if (l !== null) lats.push(l);
    last = Math.max(last, lastMsgTime(c));
  }
  const reached = spoke.length;
  const replied = spoke.filter(hasReply).length;
  const rate = reached ? replied / reached : 0;
  // Inbound threads the member never answered — reported alongside the rate, never
  // inside it. `hasReply` is true for these (they DID receive messages), so they
  // would otherwise have counted as "replied" and inflated the rate.
  const ignored = m.convos ? m.convos.length - reached : 0;
  const med = median(lats);
  let status = 'good';
  if (rate < 0.3) status = 'risk';
  else if (rate < 0.65) status = 'warn';
  else status = 'good';
  // `known` = there is a denominator. A subscriber who has messaged nobody has no
  // response rate (0/0 is undefined, not 0%), so the page must not colour them
  // "at risk", count them in the health split, or sort them alongside members who
  // really are being ignored.
  const known = reached > 0;
  return { sent, replies, reached, replied, rate, med, last, status, known, ignored };
};
